import type { CompareRoundupConfig } from "@/components/compare-landing/roundup-types";

/**
 * Config for the `/compare/monarch-alternatives` roundup, consumed by
 * `RoundupLandingPage`. Lives outside the component module so the page file
 * only exports components (react-refresh rule) — strings resolve via the
 * `compareMonarchAlternatives.*` namespace in each locale's
 * `i18n/<locale>/compare/monarch-alternatives.json`.
 *
 * The matrix rows are deliberately not the mint/ynab/quickbooks/quicken set:
 * `cost` and `ownership` are the house columns (the ranking axis and the brand
 * thesis), while `renewal`, `freePath` and `migration` exist only here, because
 * this page's reader is reacting to Monarch's tiering — a free tier withdrawn,
 * a $199.99 tier added above the plan they are on.
 *
 * `apps` order is the published ranking and must match `APP_KEYS` in
 * `compare-monarch-alternatives.meta.ts`; `compare-roundup-manifest.test.ts`
 * pins them.
 */
export const monarchAlternativesConfig: CompareRoundupConfig = {
  keyPrefix: "compareMonarchAlternatives",
  hero: {
    webShotSrc: "/img/ledger/overview-example.webp",
    trustKeys: ["trust1", "trust2", "trust3"],
  },
  apps: [
    {
      key: "beancount",
      icon: "lock",
      isBeancount: true,
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "actual",
      icon: "wallet",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "simplifi",
      icon: "target",
      offerId: "quicken-simplifi",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "copilot",
      icon: "smartphone",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "ynab",
      icon: "trending-up",
      offerId: "ynab-standard",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "empower",
      icon: "megaphone",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
  ],
  matrix: {
    rows: ["cost", "renewal", "freePath", "ownership", "migration"],
  },
  related: [
    { slug: "beancount-vs-monarch", key: "monarch" },
    { slug: "beancount-vs-actual-budget", key: "actual" },
    { slug: "empower-alternatives", key: "empowerAlternatives" },
    { slug: "copilot-alternatives", key: "copilotAlternatives" },
    {
      to: "/tools/csv-to-beancount/monarch",
      key: "monarchCsv",
      ctaKey: "toolCta.action",
    },
  ],
};
